import {
  IQuickCommercePincodeOnboardPostBody,
  IQuickCommercePincodeUpdatePutBody,
} from "@/api_framework/api_modals/QuickCommerce";
import SecondaryButton from "@/app/components/ui/Buttons/SecondaryButton";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import Input from "@/app/components/ui/Inputs/Input";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import Toggle from "@/app/components/ui/Toggles";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { useConfigurationCityListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityListAPI";
import { useConfigurationStaffClinicBasedOnCityAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationStaffClinicBasedOnCityAPI";
import { useQuickCommercePincodeDetailAPI } from "@/app/hooks/api_hooks/quick_commerce/useQuickCommercePincodeDetails";
import { useQuickCommercePincodeOnboardAPI } from "@/app/hooks/api_hooks/quick_commerce/useQuickCommercePincodeOnboardAPI";
import { useQuickCommercePincodeUpdateAPI } from "@/app/hooks/api_hooks/quick_commerce/useQuickCommercePincodeUpdate";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
export interface IAddPincodeFormFields {
  pincode: string;
  cities: string;
  clinics: Array<string>;
  latitude: number;
  longitude: number;
  cod: boolean;
  max_cod: number;
  min_cod: number;
}

const ConfigurationAddPincodeOnboard = () => {
  let pincodeDetailsFetched = false;
  const formHook = useForm<IAddPincodeFormFields>({
    mode: "onChange",
    criteriaMode: "all",
  });
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const [pincodeIdExists, setPincodeIdExists] = useState(false);
  const { forOnlyNumber, forLatitude, forLongitude } = useFormValidations();
  const { routeState } = useRouteInfo(getRouteKey("CONFIGURATION", "id"));
  const fetchCities = useConfigurationCityListAPI();
  const { clinicsByCities, execute: fetchStaffClinicBasedOnCity } = useConfigurationStaffClinicBasedOnCityAPI();
  const { cityList } = routeState?.state as IConfigurationListState;
  const cityOrgItem = useSelectMenuReducer(cityList, "name", "id");
  const clinicsOrgItem = useSelectMenuReducer(clinicsByCities, "clinic_name", "clinic_id");
  const { newPincodeId, execute: onboardPincode } = useQuickCommercePincodeOnboardAPI();
  const { updatedPincodeId, execute: updatePincode } = useQuickCommercePincodeUpdateAPI();
  const { execute: fetchPincodeDetail, pincodeDetail } = useQuickCommercePincodeDetailAPI();
  const [params, _setparams] = useSearchParams();

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    const paramsPincodeId = params?.get("pincode_id");
    if (paramsPincodeId && !pincodeDetailsFetched) {
      setPincodeIdExists(true);
      fetchPincodeDetail(parseInt(paramsPincodeId));
      pincodeDetailsFetched = true;
    }
  }, [cityList]);

  useEffect(() => {
    if (pincodeDetail?.pincode != "") {
      formHook.setValue("pincode", pincodeDetail?.pincode);
      formHook.setValue("cities", `${cityList.filter((c) => c.id == pincodeDetail?.city_name_id)?.[0]?.["id"]}`);
      formHook.setValue("clinics", pincodeDetail?.clinic_id);
      formHook.setValue("latitude", pincodeDetail?.latitude);
      formHook.setValue("longitude", pincodeDetail?.longitude);
      formHook.setValue("cod", pincodeDetail?.is_cod);
      formHook.setValue("max_cod", pincodeDetail?.max_cod);
      formHook.setValue("min_cod", pincodeDetail?.min_cod);
      fetchStaffClinicBasedOnCity([formHook.watch("cities")]);
    }
  }, [pincodeDetail]);

  const handleClinicMultiSelect = (items: ISelectMenuItemData[]) => {
    formHook.setValue(
      "clinics",
      items?.map((i) => i?.id),
    );
    clearError("clinics");
  };

  useEffect(() => {
    if (newPincodeId || updatedPincodeId) {
      navigate("/app/configuration");
    }
  }, [newPincodeId, updatedPincodeId]);

  const clearError = (fieldName) => {
    formHook?.clearErrors(fieldName);
  };

  const handleCancel = () => {
    formHook?.reset();
    navigate(getRouteKey("PINCODE_SETUP", "url"));
  };

  const Submit = (data: IAddPincodeFormFields) => {
    const obj = {
      pincode: data.pincode,
      clinic_id: data.clinics,
      latitude: +data.latitude,
      longitude: +data.longitude,
      is_cod: data.cod || false,
    };
    if (data.cod) {
      obj["max_cod"] = +data.max_cod;
      obj["min_cod"] = +data.min_cod;
    }
    const pincodeId = params?.get("pincode_id");
    if (!pincodeId || pincodeId?.length == 0) {
      obj["city_id"] = data.cities;
      onboardPincode(obj as IQuickCommercePincodeOnboardPostBody)
        // Call the API function with the payload
        .then(() => {
          // Handle successful response if needed
          // Redirect or perform any other actions as needed
          navigate("/app/configuration/pincode-setup");
        })
        .catch((error) => {
          // Handle error if the API call fails
          console.error("Error onboarding pincode:", error);
        });
    } else {
      obj["city"] = pincodeDetail?.city;
      obj["city_code"] = pincodeDetail?.city_code;
      updatePincode(obj as IQuickCommercePincodeUpdatePutBody, pincodeId)
        // Call the API function with the payload
        .then(() => {
          // Handle successful response if needed
          // Redirect or perform any other actions as needed
          navigate("/app/configuration/pincode-setup");
        })
        .catch((error) => {
          // Handle error if the API call fails
          console.error("Error updating pincode:", error);
        });
    }
  };

  return (
    <div className="grid grid-cols-5 bg-white">
      <div className="col-span-3 ">
        <div className="text-black text-base font-semibold mt-5 mx-5">Add Pincode</div>
        <div className="mx-5">
          <form onSubmit={formHook?.handleSubmit(Submit)}>
            <div className="justify-start items-baseline flex mt-5">
              <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
                <span className="text-black text-xs font-normal">Pincode</span>
                <span className="text-rose-600 text-xs font-normal">*</span>
              </div>
              <Input
                register={formHook?.register("pincode", {
                  validate: (value) => {
                    const isNumeric = /^[0-9]*$/.test(value);
                    return isNumeric || "Only numbers are allowed";
                  },
                  required: true,
                  maxLength: 6,
                  minLength: 6,
                })}
                type="text"
                fieldError={formHook?.formState?.errors?.pincode}
                errorMessages={[
                  forOnlyNumber?.errors,
                  { type: "required", message: "Pincode is required" },
                  { type: "minLength", message: "Pincode should be of 6 digits" },
                  { type: "validate", message: "Only numbers are allowed" },
                ]}
                className="h-9 w-[300px] text-sm rounded-md border-slate-400"
                maxLength={6}
                placeholder="Enter Pincode"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const inputVal = e.target?.value?.replace(/[^0-9]/g, "");
                  formHook?.setValue("pincode", inputVal);
                  clearError("pincode");
                }}
                disabled={pincodeIdExists}
              />
            </div>
            <div className="justify-start items-center flex mt-5">
              <div className="h-[38px] p-2.5 justify-start w-[150px] gap-1 flex">
                <span className="text-black text-xs font-normal">Choose Cities</span>
                <span className="text-rose-600 text-xs font-normal">*</span>
              </div>
              <div className="h-9 w-[300px]">
                <NonSearchableSelectMenu
                  selectItems={cityOrgItem}
                  onSelectItem={(items) => {
                    formHook.setValue("cities", items?.id);
                    clearError("cities");
                    fetchStaffClinicBasedOnCity([items?.id]);
                  }}
                  defaultSelected={cityOrgItem.filter((c) => c.id == pincodeDetail?.city_name_id)?.[0]}
                  placeholder="Select Cities"
                  className="text-xs"
                  showDropdownIcon={true}
                  errorMessages={[{ type: "required", message: "City is required." }]}
                  fieldError={formHook.formState.errors.cities}
                  register={formHook.register("cities", { required: true })}
                  disabled={pincodeIdExists}
                />
              </div>
            </div>
            <div className="justify-start items-center flex mt-8">
              <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
                <span className="text-black text-xs font-normal">Choose Clinics</span>
                <span className="text-rose-600 text-xs font-normal">*</span>
              </div>
              <div className="h-9 w-[300px]">
                <SearchableMultiSelectMenu
                  withDropDownSearch
                  selectItems={clinicsOrgItem}
                  onSelectItem={(items) => {
                    if (items) {
                      handleClinicMultiSelect(items);
                    }
                  }}
                  defaultSelected={clinicsOrgItem?.filter((c) => formHook?.watch("clinics")?.includes(c?.id))}
                  placeholder="Select Clinics"
                  className="text-xs"
                  showDropdownIcon={true}
                  errorMessages={[{ type: "required", message: "Clinic is required." }]}
                  fieldError={formHook.formState.errors.clinics}
                  register={formHook.register("clinics", { required: true })}
                />
              </div>
            </div>
            <div className="justify-start items-baseline flex mt-5">
              <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
                <span className="text-black text-xs font-normal">Latitude</span>
                <span className="text-rose-600 text-xs font-normal">*</span>
              </div>
              <Input
                register={formHook?.register("latitude", {
                  ...forLatitude?.validations,
                  required: true,
                })}
                step="any"
                type="number"
                className="h-9 w-[300px] text-sm rounded-md border-slate-400"
                fieldError={formHook?.formState?.errors?.latitude}
                errorMessages={[forLatitude?.errors, { type: "required", message: "Latitude is required" }]}
                placeholder="Enter Latitude"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const inputValue = e.target?.value;
                  formHook?.setValue("latitude", +inputValue);
                  clearError("latitude");
                }}
              />
            </div>
            <div className="justify-start items-baseline flex mt-5">
              <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
                <span className="text-black text-xs font-normal">Longitude</span>
                <span className="text-rose-600 text-xs font-normal">*</span>
              </div>
              <Input
                register={formHook?.register("longitude", {
                  ...forLongitude?.validations,
                  required: true,
                })}
                errorMessages={[forLongitude?.errors, { type: "required", message: "Longitude is required" }]}
                className="h-9 w-[300px] rounded-md border-slate-400 text-sm"
                fieldError={formHook?.formState?.errors?.longitude}
                placeholder="Enter Longitude"
                type="number"
                step="any"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const inputValue = e.target?.value;
                  formHook?.setValue("longitude", +inputValue);
                  clearError("longitude");
                }}
              />
            </div>
            <div className="justify-start items-center flex mt-5">
              <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
                <span className="text-black text-xs font-normal">Is COD</span>
              </div>
              <Toggle
                checked={formHook?.watch("cod") || false}
                onToggle={(state) => {
                  // console.log(state);
                  formHook?.setValue("cod", state);
                }}
              />
            </div>
            {formHook?.watch("cod") && (
              <>
                <div className="justify-start items-baseline flex mt-5">
                  <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
                    <span className="text-black text-xs font-normal">Max COD</span>
                    <span className="text-rose-600 text-xs font-normal">*</span>
                  </div>
                  <Input
                    register={formHook?.register("max_cod", {
                      required: true,
                    })}
                    type="text"
                    className="h-9 w-[300px] text-sm rounded-md border-slate-400"
                    fieldError={formHook?.formState?.errors?.max_cod}
                    errorMessages={[{ type: "required", message: "Max Cod is required" }]}
                    placeholder="Enter Max Cod"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const inputValue = e.target?.value;
                      const numericValue = inputValue?.replace(/[^0-9.]/g, "");
                      const isValidNumber = /^\d*\.?\d*$/.test(numericValue);
                      if (isValidNumber) {
                        formHook?.setValue("max_cod", +numericValue);
                        clearError("max_cod");
                      }
                    }}
                  />
                </div>
                <div className="justify-start items-baseline flex mt-5">
                  <div className="h-[38px] p-2.5 justify-start w-[150px] items-center gap-1 flex">
                    <span className="text-black text-xs font-normal">Min COD</span>
                    <span className="text-rose-600 text-xs font-normal">*</span>
                  </div>
                  <Input
                    register={formHook?.register("min_cod", {
                      required: true,
                    })}
                    type="text"
                    className="h-9 w-[300px] text-sm rounded-md border-slate-400"
                    fieldError={formHook?.formState?.errors?.min_cod}
                    errorMessages={[{ type: "required", message: "Min Cod is required" }]}
                    placeholder="Enter Min Cod"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const inputValue = e.target?.value;
                      const numericValue = inputValue?.replace(/[^0-9.]/g, "");
                      const isValidNumber = /^\d*\.?\d*$/.test(numericValue);
                      if (isValidNumber) {
                        formHook?.setValue("min_cod", +numericValue);
                        clearError("min_cod");
                      }
                    }}
                  />
                </div>
              </>
            )}
            <div className="bg-white p-3 justify-end items-center pb-4 gap-4 flex bottom-0 right-0 border-t-2 fixed w-full">
              <button
                className="rounded-md bg-opacity-10 bg-gray-500 px-10 text-sm py-[9px] text-black font-medium"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <input
                type="submit"
                className="rounded-md bg-[#193A6A] px-10 py-[9px] font-normal cursor-pointer text-white shadow-sm text-sm"
                value={"Save"}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationAddPincodeOnboard;
